import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  useTheme,
} from '@mui/material';

const GenericTable = ({
  data,
  config,
  onRowClick,
  selectedItems = [],
  handleCheckbox,
  isMobile,
  showCheckbox = false,
  isAllSelected = false,
}) => {
  const theme = useTheme();

  const renderCellContent = (item, column) => {
    if (column.render) {
      return column.render(item);
    }

    if (column.format) {
      return column.format(item[column.field]);
    }

    return item[column.field];
  };

  return (
    <TableContainer
      sx={{
        height: '100%',
        maxHeight: 'calc(100vh - 100px)',
        boxShadow: 'none',
        backgroundColor: theme.palette.background.default,
        overflow: 'auto',
        paddingBottom: '24px',
        '& .MuiTableCell-root': {
          px: isMobile ? 1 : 1.5,
          py: isMobile ? 0.75 : 1,
          fontSize: isMobile ? '0.7rem' : '0.875rem',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: '12px 16px',
          height: 'auto',
          verticalAlign: 'middle'
        },
        '&::after': {
          content: '""',
          display: 'block',
          height: '30px',
        }
      }}
    >
      <Table
        size={isMobile ? "small" : "medium"}
        sx={{
          tableLayout: 'fixed',
          minWidth: isMobile ? 600 : 900,
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: 0,
          '& tbody tr:last-child td': {
            borderBottom: 'none'
          }
        }}
      >
        <TableHead
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: theme.palette.background.paper
          }}
        >
          <TableRow>
            {showCheckbox && (
              <TableCell
                padding="checkbox"
                sx={{
                  width: '48px',
                  bgcolor: theme.palette.background.paper,
                  fontWeight: 600,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  onChange={(e) => handleCheckbox(e, null)}
                  size="small"
                />
              </TableCell>
            )}
            {config.map((column) => (
              <TableCell
                key={column.field}
                align={column.align || 'left'}
                sx={{
                  width: column.width,
                  display: column.hideOnMobile && isMobile ? 'none' : 'table-cell',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  backgroundColor: theme.palette.background.paper,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  color: theme.palette.text.primary
                }}
              >
                {column.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item, index) => (
            <TableRow
              key={item.id || index}
              onClick={() => onRowClick && onRowClick(item)}
              sx={{
                cursor: onRowClick ? 'pointer' : 'default',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              {showCheckbox && (
                <TableCell 
                  padding="checkbox"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selectedItems.some((selected) => 
                      selected.token === item.token && 
                      selected.exchange === item.exchange &&
                      selected.product === item.product
                    )}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCheckbox(e, item);
                    }}
                    size="small"
                    disabled={item.net_quantity === 0}
                    sx={{
                      cursor: item.net_quantity !== 0 ? 'pointer' : 'default',
                      opacity: item.net_quantity === 0 ? 0.5 : 1,
                    }}
                  />
                </TableCell>
              )}
              {config.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align || 'left'}
                  sx={{
                    display: column.hideOnMobile && isMobile ? 'none' : 'table-cell',
                    ...column.cellStyle,
                    ...(typeof column.dynamicStyle === 'function' ? column.dynamicStyle(item) : {})
                  }}
                >
                  {renderCellContent(item, column)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GenericTable; 